import { FIELD_HEIGHT as SHARED_FIELD_HEIGHT } from '@repo/ui'

// VERA-914 (Stage B) — shared config for the section-driven Create/Edit Owner
// form (mirrors the ProductEditPage TOC + search pattern). Colocated here so
// both CreateOwnerPage and OwnerEditPage drive the same left TOC / search
// filter / field set from one source, instead of drifting apart.

export type OwnerFormSection = {
  id: string
  label: string
  /** RHF field names this section owns — drives the TOC error marker + search-safeguard. */
  fields: string[]
  /** Extra search terms beyond the label itself. */
  keywords: string[]
}

export const OWNER_FORM_SECTIONS: OwnerFormSection[] = [
  {
    id: 'details',
    label: 'Details',
    fields: ['photo', 'full_name', 'email', 'phone', 'birthday', 'gender', 'country'],
    keywords: ['name', 'email', 'phone', 'birthday', 'date of birth', 'gender', 'country', 'profile', 'photo', 'invite'],
  },
  {
    id: 'address',
    label: 'Address',
    fields: ['address_line', 'city', 'state', 'postal_code', 'address_country'],
    keywords: ['address', 'city', 'state', 'postal', 'zip', 'country', 'location'],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    fields: ['email_marketing_consent', 'sms_marketing_consent'],
    keywords: ['marketing', 'consent', 'subscribe', 'opt-in', 'email', 'sms'],
  },
]

// Field-height parity (VERA-914 bug report): the shared `TextField` renders 49px
// and `SelectField` was hardcoded to 47, so side-by-side (e.g. Age next to Gender)
// they did not line up, and this form pulled its text fields DOWN to 47 to match.
// VRG-703 fixed the select at the source (it is now the shared FIELD_HEIGHT, 49), so
// this pins the form's fields to that same token rather than to the old 47 — which
// would now be the odd one out next to the 49px selects and country picker.
export const FIELD_HEIGHT = `${SHARED_FIELD_HEIGHT}px`
export const textFieldHeightSx = {
  '& .MuiFilledInput-root': {
    height: FIELD_HEIGHT,
    display: 'flex',
    alignItems: 'center',
  },
} as const

// VERA-914 (Stage B) — Birthday field: `DatePickerField` renders an *outlined*
// input (not the FilledInput `textFieldHeightSx` targets), so it needs its own
// height-parity rule targeting `.MuiOutlinedInput-root` to line up with the
// Gender/Country selects beside it.
export const datePickerHeightSx = {
  '& .MuiOutlinedInput-root': {
    height: FIELD_HEIGHT,
  },
} as const

export const GENDER_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Non-binary', value: 'non_binary' },
  { label: 'Other', value: 'other' },
]

export const FULL_NAME_RULES = {
  required: 'Full name is required',
  validate: (value: string) => {
    if (!value || value.trim() === '') {
      return 'Full name is required'
    }
    if (value.length < 2) {
      return 'Full name is too short'
    }
    if (value.length > 100) {
      return 'Full name is too long'
    }
    const nameRegex = /^[a-zA-Z\s\-']+$/
    if (!nameRegex.test(value)) {
      return 'Full name contains invalid characters'
    }
    return true
  },
}

export const EMAIL_RULES = {
  required: 'Email is required',
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: 'Invalid email address',
  },
  validate: (value: string) => {
    if (!value || value.trim() === '') {
      return 'Email is required'
    }
    if (value.length > 255) {
      return 'Email is too long'
    }
    return true
  },
}

export const PHONE_RULES = {
  required: 'Phone is required',
  validate: (value: string) => {
    if (!value || value.trim() === '') {
      return 'Phone is required'
    }
    if (value.length < 5) {
      return 'Phone number is too short'
    }
    if (value.length > 20) {
      return 'Phone number is too long'
    }
    // Basic phone validation - allows +, digits, spaces, dashes, parentheses.
    // NOTE: a proper phone-validation upgrade is a separate task (VERA-914 scope
    // note) — this preserves the existing behavior as-is.
    const phoneRegex = /^[+]?[0-9\s\-()]+$/
    if (!phoneRegex.test(value)) {
      return 'Phone number contains invalid characters'
    }
    return true
  },
}

// VERA-914 (Stage B) — Birthday replaces Age; no bespoke validation rule is
// needed (the picker itself enforces `disableFuture`/`maxDate`, and the field
// is optional).
